import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/shared/api/client'
import { queryKeys } from '@/shared/api/queryKeys'
import type { Call, CallDetails, OutcomeOption, PhoneCall, TestCall } from '@/shared/api/types'

export const callsApi = {
  list: (params: string, signal?: AbortSignal) =>
    api.get<Call[]>(`/calls${params ? `?${params}` : ''}`, signal),
  details: (id: number, signal?: AbortSignal) => api.get<CallDetails>(`/calls/${id}`, signal),
  startTest: () => api.post<TestCall>('/calls/test'),
  startPhone: () => api.post<PhoneCall>('/calls/phone'),
  outcomes: (signal?: AbortSignal) => api.get<OutcomeOption[]>('/calls/outcomes', signal),
  correct: (id: number, outcome: string, note: string) =>
    api.put<Call>(`/calls/${id}/outcome`, { outcome, note }),
}

export function useCalls(filter: { kind?: string; outcome?: string; bot?: string } = {}) {
  const params = new URLSearchParams()
  if (filter.kind) params.set('kind', filter.kind)
  if (filter.outcome) params.set('outcome', filter.outcome)
  // 'main' — основной бот, число — дополнительный.
  if (filter.bot) params.set('bot', filter.bot)
  const query = params.toString()

  return useQuery({
    queryKey: [...queryKeys.calls, query],
    queryFn: ({ signal }) => callsApi.list(query, signal),
  })
}

/**
 * Разговор со стенограммой и отчётом.
 *
 * Пока отчёта нет, запрос повторяется: разбор идёт в фоне и занимает
 * несколько секунд. Без этого человек, закрывший звонок, видит пустое
 * место и уходит, так и не узнав итога.
 */
export function useCall(id: number | null) {
  return useQuery({
    queryKey: [...queryKeys.call, id],
    queryFn: ({ signal }) => callsApi.details(id as number, signal),
    enabled: id !== null,
    refetchInterval: (query) => {
      const data = query.state.data
      if (!data) return false
      if (data.call.report) return false
      // Разбор ещё идёт. Раз в три секунды — чаще незачем, реже
      // ощущается как «зависло».
      return data.call.status === 'completed' ? 3000 : false
    },
  })
}

export function useOutcomes() {
  return useQuery({
    queryKey: queryKeys.outcomes,
    queryFn: ({ signal }) => callsApi.outcomes(signal),
    // Список итогов не меняется без выкатки — держим его весь сеанс.
    staleTime: Infinity,
  })
}

export function useCorrectOutcome(callId: number) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ outcome, note }: { outcome: string; note: string }) =>
      callsApi.correct(callId, outcome, note),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.call })
      void queryClient.invalidateQueries({ queryKey: queryKeys.calls })
    },
  })
}
